import React from "react";
import { Star, Plus, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from "@/components/ui/dialog";
import { UserListPanel } from "@/components/UserListPanel";
import { BottomSheet } from "@/components/story/StorySheets";
import { CLOSE_FRIENDS_COLOR } from "@/lib/postAudience";

// Lot 655 — « Amis proches » : liste du compte (users.close_friends) → audience « close_friends » (stories, publications, Reels), anneau vert.
export const useCloseFriendsCount = () => { const { user } = useApp(); return (user?.close_friends || []).length; };

const useLabels = () => {
  const { t } = useApp();
  return {
    add: <><Plus size={12} /> {t("close_friends.add", "Ajouter")}</>,
    remove: <><X size={12} /> {t("close_friends.remove", "Retirer")}</>,
    listTitle: t("close_friends.list_title", "Dans la liste"),
    empty: t("close_friends.empty", "Ta liste est vide pour l'instant."),
    added: t("close_friends.added", "{name} fait partie de tes amis proches"),
    removed: t("close_friends.removed", "{name} ne fait plus partie de tes amis proches"),
  };
};

export const CloseFriendsPanel = ({ tone = "light" }) => <UserListPanel basePath="/users/me/close-friends" testPrefix="close-friends" labels={useLabels()} tone={tone} accent={CLOSE_FRIENDS_COLOR} />;

// Lien « Gérer la liste · n » sous l'option « Amis proches ».
export const CloseFriendsLink = ({ onClick, tone = "light", testId = "close-friends-link" }) => {
  const { t } = useApp();
  const count = useCloseFriendsCount();
  const dark = tone === "dark";
  return (
    <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 h-7 transition-colors ${dark ? "bg-white/10 text-white/85 hover:bg-white/15" : "hover:opacity-80"}`}
      style={dark ? undefined : { color: CLOSE_FRIENDS_COLOR, background: "var(--yalola-surface)" }} data-testid={testId} data-count={count}>
      <Star size={13} fill="currentColor" /> {t("close_friends.manage", "Gérer la liste")}<span className={`rounded-full px-1.5 text-[10px] ${dark ? "bg-white/20" : "text-white"}`} style={dark ? undefined : { background: CLOSE_FRIENDS_COLOR }} data-testid={`${testId}-count`}>{count}</span>
    </button>
  );
};

export const CloseFriendsDialog = ({ open, onOpenChange }) => {
  const { t } = useApp();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="sheet" className="sm:max-w-md z-[97]" overlayClassName="z-[96]" data-testid="close-friends-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Star size={18} fill={CLOSE_FRIENDS_COLOR} style={{ color: CLOSE_FRIENDS_COLOR }} /> {t("close_friends.title", "Amis proches")}</DialogTitle>
          <DialogDescription>{t("close_friends.desc", "Partage une story ou une publication à ce cercle choisi grâce à l'audience « Amis proches » (anneau vert). Les personnes ajoutées ne sont pas prévenues.")}</DialogDescription>
        </DialogHeader>
        <DialogBody>{open && <CloseFriendsPanel tone="light" />}</DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export const CloseFriendsSheet = ({ open, onClose, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("close_friends.title", "Amis proches")} testId="story-fs-close-friends-sheet" maxH="85dvh">
    <p className="text-xs text-white/65 mb-3">{t("close_friends.desc", "Partage une story ou une publication à ce cercle choisi grâce à l'audience « Amis proches » (anneau vert). Les personnes ajoutées ne sont pas prévenues.")}</p>
    <CloseFriendsPanel tone="dark" />
  </BottomSheet>
);

// Carte Réglages → Confidentialité.
export const CloseFriendsCard = () => {
  const { t } = useApp();
  return (
    <div className="yalola-card p-5" id="amis-proches" data-testid="settings-close-friends-card">
      <div className="flex items-center gap-2 mb-1">
        <Star size={18} fill={CLOSE_FRIENDS_COLOR} style={{ color: CLOSE_FRIENDS_COLOR }} />
        <div className="font-bold font-display text-lg">{t("close_friends.title", "Amis proches")}</div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--yalola-text-muted)" }}>{t("close_friends.desc", "Partage une story ou une publication à ce cercle choisi grâce à l'audience « Amis proches » (anneau vert). Les personnes ajoutées ne sont pas prévenues.")}</p>
      <CloseFriendsPanel tone="light" />
    </div>
  );
};
